import React, { useMemo } from 'react';
import { usePos } from '../context/PosContext';
import { formatDate } from '../services/datetime';
import './Sections.css';

const dayKey = (iso: string) => formatDate(iso);

// Non-GST summary: gross sales by day without tax breakup.
const NgstSummary: React.FC = () => {
  const { sales, formatMoney } = usePos();

  const rows = useMemo(() => {
    const map = new Map<string, { bills: number; items: number; gross: number }>();
    sales
      .filter((s) => s.status === 'completed')
      .forEach((s) => {
        const k = dayKey(s.timestamp);
        const r = map.get(k) ?? { bills: 0, items: 0, gross: 0 };
        r.bills += 1;
        r.items += s.items.reduce((a, i) => a + i.quantity, 0);
        r.gross += s.subtotal - s.discount;
        map.set(k, r);
      });
    return Array.from(map.entries())
      .map(([date, v]) => ({ date, ...v }))
      .reverse();
  }, [sales]);

  const grand = rows.reduce((a, r) => ({ bills: a.bills + r.bills, items: a.items + r.items, gross: a.gross + r.gross }), { bills: 0, items: 0, gross: 0 });

  return (
    <div className="section-page">
      <div className="page-header">
        <h1>NGST Summary</h1>
      </div>
      <div className="section-card">
        {rows.length === 0 ? (
          <p className="muted">No sales yet.</p>
        ) : (
          <table className="section-table">
            <thead>
              <tr>
                <th>Date</th>
                <th className="num-cell">Bills</th>
                <th className="num-cell">Items</th>
                <th className="right">Gross (excl. tax)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.date}>
                  <td>{r.date}</td>
                  <td>{r.bills}</td>
                  <td>{r.items}</td>
                  <td className="right" style={{ fontWeight: 700, color: 'var(--color-primary-dark)' }}>
                    {formatMoney(r.gross)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {rows.length > 0 && (
          <div className="totals-line">
            <span>Bills: {grand.bills}</span>
            <span>Items: {grand.items}</span>
            <span>Gross: {formatMoney(grand.gross)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default NgstSummary;
